import { KrCard } from '@/common/card/KrCard';
import { Form } from '@/common/form/Form';
import { FormFieldGroupContainer } from '@/common/form/FormFieldGroupContainer';
import { DateFormField } from '@/common/form/formFields/DateFormField';
import { ContentBox } from '@/common/layout/ContentBox';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { faPlus, faTrash } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, Chip, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

const EMPTY_FORM = { startDate: null, endDate: null };

const overlaps = (a, b) => a.startDate <= b.endDate && b.startDate <= a.endDate;

const dayAfter = (date) => Number(dayjs(String(date), 'YYYYMMDD').add(1, 'day').format('YYYYMMDD'));

// Groups (entity + apprGroup) left with fewer available approvers than required on some day in the window
const coverageGaps = (window, assignments, groupToRequired, groupRows, allOoos, username) => {
  const gaps = [];
  assignments.forEach((assignment) => {
    const key = `${assignment.entity}|${assignment.apprGroup}`;
    const required = groupToRequired[key];
    if (required == null) return;
    const others = groupRows.filter(
      (row) => row.username !== username && row.apprGroup === assignment.apprGroup && row.entity === assignment.entity,
    );
    const otherOoos = allOoos.filter((ooo) => others.some((row) => row.username === ooo.username));

    // Availability only changes where another approver's OOO starts or ends, so checking those days suffices
    const checkDates = new Set([window.startDate]);
    otherOoos.forEach((ooo) => {
      if (ooo.startDate > window.startDate && ooo.startDate <= window.endDate) checkDates.add(ooo.startDate);
      const back = dayAfter(ooo.endDate);
      if (back > window.startDate && back <= window.endDate) checkDates.add(back);
    });

    const short = [...checkDates].some((date) => {
      const oooUsernames = new Set(
        otherOoos.filter((ooo) => ooo.startDate <= date && date <= ooo.endDate).map((ooo) => ooo.username),
      );
      return others.filter((row) => !oooUsernames.has(row.username)).length < required;
    });
    if (short) gaps.push(assignment);
  });
  return gaps;
};

export const OutOfOfficeDetail = ({ userObj: user }) => {
  const rsrc = useRsrc();
  const formRef = useRef();

  // APPR_ROUTE_USER rows for this user; OOO windows have no effect without at least one
  const [assignments, setAssignments] = useState();
  // APPR_ROUTE_USER_OOO windows for this user, ordered by start date
  const [ooos, setOoos] = useState();
  // Staffing context for the coverage warning: `entity|apprGroup` -> required count, rows in those groups, all OOO windows
  const [groupToRequired, setGroupToRequired] = useState({});
  const [groupRows, setGroupRows] = useState([]);
  const [allOoos, setAllOoos] = useState([]);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const expr = ExprUtils.whereParams('username', user.username);
    Promise.all([
      rsrc.rows('skdr', 'APPR_ROUTE_USER', { expr }),
      rsrc.rows('skdr', 'APPR_ROUTE_USER_OOO', { expr }),
    ]).then(([assignmentRows, oooRows]) => {
      setAssignments(assignmentRows);
      setOoos([...oooRows].sort((a, b) => a.startDate - b.startDate));
      setFormValues(EMPTY_FORM);

      const entities = [...new Set(assignmentRows.map((row) => row.entity))];
      if (entities.length === 0) return;
      Promise.all([
        ...entities.map((entity) =>
          rsrc
            .data('skdr', 'APPR_ROUTE_DATA_PLUGIN', { entity, entityObjs: [] }, entity)
            .then((routes) => [entity, routes]),
        ),
        rsrc.rows('skdr', 'APPR_ROUTE_USER', { expr: ExprUtils.whereParams('entity', entities) }),
        rsrc.rows('skdr', 'APPR_ROUTE_USER_OOO', {}),
      ]).then((results) => {
        const everyOoo = results.pop();
        const rows = results.pop();
        // Same demand model as PymtApprovalDialog: a route's demand is summed across steps, groups keep the max route
        const required = {};
        results.forEach(([entity, routes]) =>
          routes.forEach((route) => {
            const routeRequired = {};
            route.steps.forEach((step) =>
              Object.entries(step.apprGroupToJobIds).forEach(([group, jobIds]) => {
                routeRequired[group] = (routeRequired[group] ?? 0) + jobIds.length;
              }),
            );
            Object.entries(routeRequired).forEach(([group, count]) => {
              const key = `${entity}|${group}`;
              required[key] = Math.max(required[key] ?? 0, count);
            });
          }),
        );
        setGroupToRequired(required);
        setGroupRows(rows);
        setAllOoos(everyOoo);
      });
    });
  };

  useEffect(load, [user.username]);

  const fieldDefs = useMemo(
    () => [
      {
        field: 'startDate',
        displayName: 'Out of Office From',
        type: 'DATE',
        validators: [(value) => (!value ? 'Start date is required' : null)],
      },
      {
        field: 'endDate',
        displayName: 'Out of Office Until',
        type: 'DATE',
        validators: [
          (value) => (!value ? 'End date is required' : null),
          (value, values) =>
            value && values.startDate && Number(value) < Number(values.startDate) ?
              'End date must be on or after the start date'
            : null,
          (value, values) => {
            if (!value || !values.startDate) return null;
            const candidate = { startDate: Number(values.startDate), endDate: Number(value) };
            return (ooos ?? []).some((ooo) => overlaps(ooo, candidate)) ? 'Overlaps an existing window' : null;
          },
        ],
      },
    ],
    [ooos],
  );

  const today = Number(DateUtils.today());
  const current = ooos?.find((ooo) => ooo.startDate <= today && today <= ooo.endDate);
  const next = ooos?.find((ooo) => ooo.startDate > today);
  const fullName = `${user.firstName} ${user.lastName}`;

  const pendingWindow =
    formValues.startDate && formValues.endDate && Number(formValues.endDate) >= Number(formValues.startDate) ?
      { startDate: Number(formValues.startDate), endDate: Number(formValues.endDate) }
    : null;
  const pendingGaps =
    pendingWindow ?
      coverageGaps(pendingWindow, assignments ?? [], groupToRequired, groupRows, allOoos, user.username)
    : [];

  const add = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }
    setSaving(true);
    rsrc
      .save('skdr', 'APPR_ROUTE_USER_OOO', [
        { username: user.username, startDate: Number(formValues.startDate), endDate: Number(formValues.endDate) },
      ])
      .then(load)
      .finally(() => setSaving(false));
  };

  const remove = (ooo) => {
    setSaving(true);
    rsrc
      .delete('skdr', 'APPR_ROUTE_USER_OOO', ooo.id)
      .then(load)
      .finally(() => setSaving(false));
  };

  const statusChip = (ooo) => {
    if (ooo.endDate < today) return <Chip size="small" label="Ended" />;
    if (ooo.startDate > today) return <Chip size="small" color="info" label="Upcoming" />;
    return <Chip size="small" color="warning" label="Active" />;
  };

  if (!assignments || !ooos) return;

  return (
    <ContentBox>
      <KrCard minHeight={0} title="Out of Office">
        {assignments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {fullName} is not currently set up to approve anything. Any out-of-office dates saved here will be used if
            they are added as an approver later.
          </Alert>
        )}
        {current && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {fullName} is out of office until {DateUtils.format(current.endDate)}. Anything needing their approval will
            be sent to someone else until then.
          </Alert>
        )}
        {!current && next && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {fullName} will be out of office from {DateUtils.format(next.startDate)} to {DateUtils.format(next.endDate)}
            .
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Out-of-office windows
        </Typography>
        {ooos.length === 0 ?
          <Typography variant="body2" sx={{ color: 'text.disabled', mb: 2 }}>
            No out-of-office windows scheduled
          </Typography>
        : <List dense disablePadding sx={{ mb: 2 }}>
            {ooos.map((ooo) => (
              <ListItem
                key={ooo.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={saving}
                    onClick={() => remove(ooo)}
                    data-test-id={`ooo-remove-${ooo.id}`}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${DateUtils.format(ooo.startDate)} – ${DateUtils.format(ooo.endDate)}`}
                  secondary={statusChip(ooo)}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            ))}
          </List>
        }

        <Form ref={formRef} formValues={formValues} setFormValues={setFormValues}>
          <FormFieldGroupContainer>
            {fieldDefs.map((fieldDef) => (
              <DateFormField key={fieldDef.field} fieldDef={fieldDef} />
            ))}
          </FormFieldGroupContainer>
        </Form>

        {pendingGaps.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-test-id="ooo-coverage-warning">
            While {fullName} is away there will be no one available to approve{' '}
            {pendingGaps.map((gap) => `${gap.entity} (${gap.apprGroup})`).join(', ')}. Approvals may be delayed until
            someone else is added or they return.
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            color="primary"
            variant="contained"
            sx={{ minWidth: '100px' }}
            disabled={saving}
            onClick={add}
            data-test-id="ooo-add-button"
          >
            <Box component="span" sx={{ mr: 1.3 }}>
              <FontAwesomeIcon icon={faPlus} />
            </Box>
            Add Window
          </Button>
        </Box>

        {assignments.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
              Applies to these approval assignments
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {assignments.map((row) => (
                <Chip key={row.id} size="small" label={`${row.entity} · ${row.apprGroup} · #${row.approvalPriority}`} />
              ))}
            </Box>
          </>
        )}
      </KrCard>
    </ContentBox>
  );
};
